import { createFileRoute, redirect } from '@tanstack/react-router'

import { AppLayout, AppShellSkeleton } from '@/app/layouts'
import { requireSession } from '@/app/router/session'

const ACCOUNT_PATH = '/account'

/**
 * A pathless layout: it adds no URL segment, only the guard and the chrome.
 * Every protected route is a child of this one.
 *
 * Access is enforced here, in `beforeLoad` — never with an early return inside
 * a page component. The router shows the app shell skeleton while this
 * resolves, so a reload never flashes a redirect to login.
 */
export const Route = createFileRoute('/_auth')({
  beforeLoad: async ({ context, location }) => {
    const session = await requireSession(context.queryClient, location.href)

    // goauth issues tokens to an account that must change its password and
    // gates nothing afterwards — it returns `ErrMustChangePassword` from
    // nowhere. This redirect is the entire enforcement, so it cannot be a
    // banner: an administrator's starter password would otherwise live forever.
    if (session.mustChangePassword && location.pathname !== ACCOUNT_PATH) {
      throw redirect({ to: ACCOUNT_PATH, replace: true })
    }

    return session
  },
  component: AppLayout,
  // This is the one route whose pending state has no shell around it, so it is
  // the one route that draws the shell — the same rail and column as the
  // `.boot` markup in `index.html`, which is what it takes over from. Every
  // route below this one gets the router's `PagePending` instead.
  pendingComponent: AppShellSkeleton,
  // No delay, unlike the 300ms the router defaults to: there is no previous
  // page to hold here, so a delay is a blank frame rather than a kept one.
  pendingMs: 0,
})
